/**
 * DynLiveFill — the Dynamic pane for a Featured-Plan / Floorplan-cards or Listing
 * block: the selected-cards panel, plus the stepped wizard it opens. 2026-08-20.
 *
 * THIS EXISTS TO MAKE THE HANDOFF ONE LINE. `Inspector.tsx` is being edited by
 * another session, so the port cannot wire itself in. Its props are LiveFillField's
 * props exactly (`mode`, `onFill`), so landing the port is a single substitution
 * inside Inspector's `liveFill` helper (L1109) — `<LiveFillField` → `<DynLiveFill`
 * — with no other change to that file.
 *
 * WHAT IT REPLACES. `LiveFillField` (Inspector.tsx L725) is a search box, a chip row
 * and a "Fill from live content" button: no photo cards, no communities → plans →
 * homesites steps, no FEATURE LEVEL, no caps, no way to see or un-pick what was
 * chosen. This is the monolith's actual surface (index.html __dynModal L10278 +
 * __dynThumbs L10245 + the __dynPane header L12160).
 *
 * THE STANDING PRODUCT RULE IS PRESERVED (Jack, repeated; monolith comment L12163):
 * switching a section to Dynamic must NOT auto-fill. `onFill` is called from ONE
 * place only — the wizard's Done — so content lands on an explicit pick and nothing
 * else.
 */
import { useEffect, useMemo, useState } from "react";
import DynFeaturePicker, { type DynPickResult } from "./DynFeaturePicker";
import DynSelectedPanel from "./DynSelectedPanel";
import { divComms } from "../services/snapshots";
import type { Pack, FillLevel } from "../services/dynamicFill";

interface Props {
  /** the field's livefill mode — "rows" for the Listing table, else the Featured
   *  cards. Same contract LiveFillField had. */
  mode: string;
  /**
   * hands the caller the packs + the level to fill from — plus, as of 2026-08-20,
   * WHAT WAS PICKED.
   *
   * THE THIRD ARGUMENT IS THE POINT. The first two say "here is a community's live
   * content, at this level"; only the third says WHICH floorplan or WHICH homes the
   * requester chose. An ECH block fills Layne's declared tokens from ONE plan, so
   * without the picks there is nothing to fill them from and the wizard's Done
   * could not change the canvas — which is what "dynamic is not set up" looked
   * like from the outside.
   *
   * ADDITIVE ON PURPOSE: a handler that only takes `(packs, level)` still type-
   * checks, so the two legacy branches in `Inspector.liveFill` are untouched.
   */
  onFill: (packs: Pack[], level: FillLevel, picks: { comms: string[]; plans: string[]; homes: string[] }) => void;
  /** OPTIONAL second edit, worth making: `divisionCode={echCtx?.divisionCode}`
   *  scopes the community step to Step 1's division instead of the full list. */
  divisionCode?: string;
  /**
   * ── `commNames` IS GONE, AND THIS IS THE NOTE THAT REPLACES IT (2026-08-20) ──────
   *
   * It existed for one day. It scoped the community step to Step 1's single pick,
   * because with the full division list a requester could pick "Portland" out of
   * Apalachee Hills while Step 1's community was Arlington Heights: the pick stored
   * correctly (`__planPick = "26217|pn|7653|Portland"`), the selected-cards panel
   * updated to "Portland · Apalachee Hills · 2,522 sq ft", and THE CANVAS DID NOT
   * MOVE — `echDynVal`'s plan branch resolved only against `ctx.pack`.
   *
   * That was a fix for the SYMPTOM. Jack, the next day: "i want the right panel to
   * have the other view where i don't even need a community — i click the + button
   * and can select the info myself." So the cause is fixed instead: the pick carries
   * its own community CODE, `echPacks` now includes the wizard's own packs, and
   * `echBlockLevel` reads a picked block at community level whatever Step 1 says.
   * The community step is the division's list again — which is what the live monolith
   * does (verified in Chrome 2026-08-20: focus "Market", no community, and its
   * community step still listed all 50 ATL communities).
   */
  /**
   * THE INVENTORY STEP 1 ALREADY LOADED — and, for a MASTERPLAN, its child
   * collections' too. Added 2026-08-20 for "full plan should pull in all related
   * collections".
   *
   * `commNames` above scoped the community step to Step 1's pick, which was right and
   * was not enough: a masterplan's own /sfmc-content pack has NO plans (they live on
   * `Village - Timber Ridge`, `50' & 60' - Timber Ridge`, …), so scoping the step to
   * "Timber Ridge" and letting the wizard resolve that name itself produced one empty
   * pack and a Floorplans step with zero cards. `echPacks(ctx)` is the masterplan's
   * pack plus one per collection, already fetched by `useEchDyn`; handing it in is
   * both the fix and the reason there is no second fetch layer.
   *
   * A SEED, NOT AN OVERRIDE, since 2026-08-20: `fixedFor` below says which community
   * names these answer for, and anything else the requester picks is fetched.
   */
  fixedPacks?: Pack[];
  /** the community names `fixedPacks` stands for — Step 1's own picks. See the prop
   *  of the same name on `DynFeaturePicker` for why a name match alone is not enough. */
  fixedFor?: string[];
  /** the monolith's toast(); without it a cap refusal is silent but still refused */
  onToast?: (msg: string) => void;
  /**
   * ── `blockedReason` IS GONE (2026-08-20) ────────────────────────────────────────
   *
   * The pane used to refuse to pick unless Step 1 was scoped to exactly one
   * community, first by HIDING itself (Jack: "dynamic is not set up", then "still not
   * seeing this") and then, when that was fixed, by rendering a greyed `.dynp-blocked`
   * header over a sentence naming what to go and do on Step 1.
   *
   * Both were the same mistake: treating a resolver limitation as a product rule. The
   * limitation is gone — see `echBlockLevel` and `EchCtx.pickPacks` — and so is the
   * refusal. The wizard's OWN community step is where the community gets chosen, which
   * is exactly what the monolith does and exactly what Jack asked for. The prop, its
   * render branch and its `.dynp-blocked` / `.dynp-why` CSS were all removed rather
   * than left unused: a control that cannot fire is the dead UI this codebase deletes
   * on sight.
   */
  /**
   * THE LEVEL THE BLOCK IS ALREADY AT — so a reopened panel comes back where the
   * requester left it (2026-08-20, Jack: "should be dynamic … communities, floor plan
   * or homesites").
   *
   * The level used to be component state seeded from `mode` and thrown away on
   * unmount, so switching to Homesites, pressing Done and clicking the block again
   * showed a Floorplans pane over a canvas full of homes — two renderings of the same
   * decision disagreeing. The caller now stores it on the block (`__featLevel`) and
   * hands it back in. Absent → the `mode` default, which is what the legacy blocks
   * (with no stored level) get.
   */
  featLevel?: string;
  /**
   * A ONE-SLOT STEP: the plans/homes grids become single-select (2026-08-20).
   *
   * WHY IT HAD TO EXIST. Layne's `ECH_Featured1CTA` renders exactly ONE card, and the
   * fill takes the FIRST pick — so the wizard's own cap of 10 plans / 15 homes let a
   * requester select five floorplans, watch five tiles appear in the selected-cards
   * panel, and see one of them on the canvas. That is the "two renderings must move
   * together" fault, and it was already live for plans before this change. The Listing
   * table has ten real slots and passes no cap, so it is untouched.
   */
  pickCap?: number;
  /**
   * WHAT THE BLOCK ALREADY HOLDS, in the WIZARD's key shape — 2026-08-20, and the
   * third time this same fault has been closed here.
   *
   * THE DEFECT, found by driving the real path in Chrome after the pane had already
   * been called working: Step 1 on Market with no community, pick Apalachee Hills →
   * Portland, Done. The canvas correctly read "Portland · 2522 sq ft · 4 beds". Click
   * Manual, click Dynamic — and the pane read "Floorplans · 0 selected" with no tile,
   * over a canvas still showing Portland. Two renderings of one pick disagreeing.
   *
   * The cause is structural: the picks lived ONLY in this component's state, and in
   * Manual mode this component is not rendered at all, so switching modes (or simply
   * re-selecting the block, since the Inspector is keyed by block id) unmounted it and
   * the picks went with it. `featLevel` above was moved onto the block earlier the same
   * day for exactly this reason; the picks had not been.
   * `Inspector` reads `__planPick` / `__homePick` off the block and translates them
   * with `pickKeysBack`, so this is the block's own durable answer, not a second copy.
   */
  storedPicks?: { plans?: string[]; homes?: string[] };
  /**
   * WIZARD-ONLY MODE (2026-08-20, Jack on the Featured Plan container: "should also
   * be able to select feature plan and be able to switch between floor plans and
   * communities"). The plan container's dashed Choose card opens the SAME stepped
   * wizard the Dynamic pane uses — one picker, two doors — so this instance renders
   * no selected-cards panel (`hidePanel`), opens immediately (`autoOpen`), and tells
   * the container when it closed so it can unmount (`onWizClose`). Picks and level
   * persist on the BLOCK, so nothing is lost when this unmounts.
   */
  hidePanel?: boolean;
  autoOpen?: boolean;
  onWizClose?: () => void;
  /** a SECTION add (2026-08-22): the section's community name + the blank-card escape */
  scopeName?: string;
  onHandType?: () => void;
}

export default function DynLiveFill({ mode, onFill, divisionCode, fixedPacks, fixedFor, onToast, featLevel, pickCap, storedPicks, hidePanel, autoOpen, onWizClose, scopeName, onHandType }: Props) {
  /* the section this pane belongs to, in the monolith's ids: rows → the Listing
     table (lst), everything else → the Featured block (fp). Its DEFAULT feature
     level is the same as the monolith's `__dyn.secLevel` default (L9862): the
     Featured block shows plans, the Listing table shows homes. */
  const sec: "fp" | "lst" = mode === "rows" ? "lst" : "fp";
  /* THE STORED LEVEL WINS OVER THE SECTION DEFAULT. Validated here rather than
     trusted: an unreadable value falls back to the section's own default, the same
     rule `echFeatOf` applies on the service side, so a hand-edited document cannot
     leave the pane in a level that does not exist. */
  const [level, setLevel] = useState<FillLevel>(
    featLevel === "plans" || featLevel === "homes" || featLevel === "comms"
      ? featLevel
      : (sec === "lst" ? "homes" : "plans"),
  );
  const [open, setOpen] = useState(!!autoOpen);
  const [packs, setPacks] = useState<Pack[]>([]);
  const [picks, setPicks] = useState<{ comms: string[]; plans: string[]; homes: string[] }>(() => ({
    comms: [], plans: (storedPicks?.plans || []).slice(), homes: (storedPicks?.homes || []).slice(),
  }));

  /* THE BLOCK'S OWN PICK IS THE TRUTH, AND IT KEEPS BEING THE TRUTH (2026-08-20).
     Seeding at mount is not enough on its own: on a reopened job the PACK a plan pick
     resolves against arrives a moment after the panel does, so `pickKeysBack` returns
     [] on the first render and the real answer only on a later one. Re-syncing on the
     stored string means the tile appears when the inventory lands.
     THIS CANNOT FIGHT A LOCAL EDIT, which is what makes it safe: every local change
     (Done, or a tile's ×) calls `onFill`, the caller writes the pick to the block, and
     the value that comes back through this prop is the one just set. `comms` is left
     alone — it is wizard-local and no block stores it. */
  const storedKey = JSON.stringify([storedPicks?.plans || [], storedPicks?.homes || []]);
  useEffect(() => {
    setPicks((p) => ({ ...p, plans: (storedPicks?.plans || []).slice(), homes: (storedPicks?.homes || []).slice() }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedKey]);

  /* THE PANE DRAWS FROM EVERY PACK IN HAND, not just the ones this wizard run loaded.
     `fixedPacks` is `echPacks(ctx)` — Step 1's community, its collections, and every
     community picked in the wizard this session (`EchCtx.pickPacks`) — which is what
     lets a re-mounted pane resolve a stored pick into a tile at all. The wizard's own
     packs go LAST and only add codes: a run that narrowed to one community must not
     remove the pack the stored pick needs. */
  const panePacks = useMemo(() => {
    const out = (fixedPacks || []).filter((p) => p && p.code).slice();
    const seen: Record<string, 1> = {};
    out.forEach((p) => { seen[p.code] = 1; });
    packs.forEach((p) => { if (p && p.code && !seen[p.code]) { seen[p.code] = 1; out.push(p); } });
    return out;
  }, [fixedPacks, packs]);

  const done = (r: DynPickResult) => {
    setPacks(r.packs);
    setPicks({ comms: r.comms, plans: r.plans, homes: r.homes });
    setOpen(false);
    /* the ONE call site — see the standing rule in the header comment */
    onFill(r.packs, r.level, { comms: r.comms, plans: r.plans, homes: r.homes });
    if (onWizClose) onWizClose();
  };

  /* dropping a tile re-fills from the smaller pick list, so the panel and the canvas
     move together. Two renderings of the same picks must never disagree.
     THE NEW LIST HAS TO BE THE ONE THAT GOES OUT — `picks` is still the old value
     on this tick (setState is not synchronous), so `next` is passed rather than
     read back. Before the third argument existed this call carried no picks at all,
     so removing a tile emptied the panel and left the canvas as it was: the exact
     two-renderings-disagree fault. */
  const remove = (key: string) => {
    const k = level === "homes" ? "homes" : "plans";
    const next = { ...picks, [k]: (picks[k] || []).filter((x) => x !== key) };
    setPicks(next);
    /* `panePacks`, not `packs`: the tile being removed may have come from a pack this
       wizard run never loaded (a stored pick re-seeded on mount), and `homePickList`
       verifies every key against the packs it is handed — so passing the narrower list
       would silently drop the SURVIVING picks too. */
    onFill(panePacks, level, next);
  };

  return (
    <>
      {!hidePanel && <DynSelectedPanel
        level={level}
        packs={panePacks}
        picks={picks}
        featured={panePacks.map((p) => ({ name: p.name, code: p.code }))}
        onOpen={() => setOpen(true)}
        onRemove={remove}
        /* NO CLEAR BUTTON, DELIBERATELY — and this is a real gap, not an oversight.
           The monolith's Clear calls `__dynRestore(secId)`, which puts back the
           placeholder markup it saved in `window.__dynOrig` when the section first
           went Dynamic (index.html L10240, L12155–12160). This repo has no port of
           that save/restore pair, so the only things Clear could do here are write
           an EMPTY fill or re-run the level's default fill — and both would claim to
           have restored the template's own content without doing it. Ship Clear when
           __dynOrig is ported; leave it off until then. */
      />}
      {open && (
        <DynFeaturePicker
          sec={sec}
          level={level}
          onLevel={setLevel}
          /* THE DIVISION'S COMMUNITIES, ALWAYS — the monolith's `commList()`, which
             reads the form's own community <select> options and is therefore the
             division roster whatever Step 1 selected out of it. See the `commNames`
             note above for the day this was scoped to Step 1's pick and why it isn't
             any more. */
          /* STEP 1'S OWN PICK FIRST, THEN THE DIVISION. Not decoration: `divComms` is
             a bundled SNAPSHOT of each division's roster, so a community added to
             Airtable since the last refresh — or one the form let through from another
             division — would be missing from the step the requester was just sent to.
             The monolith cannot have this problem (its list IS the form's own <select>
             options), so prepending the picks is how the same guarantee is kept here.
             `DynFeaturePicker` dedupes by lower-cased name, so an already-listed
             community appears once. */
          divisionComms={(fixedFor || []).filter(Boolean).concat(divComms(divisionCode || ""))}
          /* …and named separately, so the grouped-by-area step can put them in a
             leading "On this request" section instead of relying on array order
             (2026-08-22) */
          onRequest={(fixedFor || []).filter(Boolean)}
          /* the division whose community heroes to look up — see the prop's comment in
             DynFeaturePicker for why the browse grid had no thumbnails without it */
          divisionCode={divisionCode}
          picks={picks}
          /* the wizard opens with every pack in hand, so a reopen on a stored pick can
             draw its community's floorplans without a refetch */
          packs={panePacks}
          fixedPacks={fixedPacks}
          fixedFor={fixedFor}
          onDone={done}
          onClose={() => { setOpen(false); if (onWizClose) onWizClose(); }}
          onToast={onToast}
          pickCap={pickCap}
          scopeName={scopeName}
          onHandType={onHandType}
        />
      )}
    </>
  );
}
